import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TaskSetView } from 'src/components/task-set/task-set-view';
import { TaskScope } from 'src/models/global-enum';
import { ApiRequestParams, EventTaskSpawnRequest } from 'src/models/global-interfaces';
import {
  taskSetTableApiRequestParams,
  TaskSetTableRecord,
  TaskSetBase,
  TaskSetInfo,
  EditTaskSet,
  TaskSetOptions,
  DeployTaskSetRequest,
  SpawnTaskSetRequest,
  TaskSetRecallRequest,
  ClosedTaskSetResponse,
  ReassignTaskSetRequest,
  CopyTaskSetRequest,
  TaskSetNameAdditionRequest,
  UpdateTaskSetRequest,
} from 'src/models/task-set.model';
import { ClosedTaskRequest, EventTaskInfoResponse, TaskUpdateTags } from 'src/models/task.model';
import { createHttpParams } from './utilities';

@Injectable()
export class TaskSetService {
  private readonly http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getTaskSets = (params: ApiRequestParams = taskSetTableApiRequestParams) =>
    this.http.get<TaskSetTableRecord[]>(`${this.apiUrl}task-sets`, {
      params: createHttpParams(params),
    });

  updateTaskSet = (taskSetId: number, taskSet: UpdateTaskSetRequest) =>
    this.http.put<unknown>(`${this.apiUrl}task-sets/${taskSetId}`, taskSet);

  updateTaskSetNameAddition = (
    taskSetId: number,
    taskNameAdditionRequest: TaskSetNameAdditionRequest,
  ) =>
    this.http.patch<TaskSetInfo>(
      `${this.apiUrl}task-sets/update-name-addition/${taskSetId}`,
      taskNameAdditionRequest,
    );

  updateTaskSetTags = (taskSetId: number, taskSet: TaskUpdateTags) =>
    this.http.put<unknown>(`${this.apiUrl}task-sets/${taskSetId}`, taskSet);

  createTaskSet = () =>
    this.http.post<TaskSetBase>(`${this.apiUrl}task-sets`, {
      task_set_name: 'New Task Set Template',
    });

  getTaskSetInfo = (taskSetID: number) =>
    this.http.get<TaskSetInfo>(`${this.apiUrl}task-sets/${taskSetID}/taskSetInfo`);

  getTaskSetForEdit = (taskSetID: number) =>
    this.http.get<EditTaskSet>(`${this.apiUrl}task-sets/${taskSetID}/editTaskSet`);

  getTaskSetOptions = (taskSetID: number) =>
    this.http.get<TaskSetOptions>(`${this.apiUrl}task-sets/${taskSetID}/taskSetSettings`);

  taskSetDeployment = (payload: DeployTaskSetRequest) =>
    this.http.post<unknown>(`${this.apiUrl}deploy-task-set`, payload);

  spawnTaskSet = (payload: SpawnTaskSetRequest) =>
    this.http.post<unknown>(`${this.apiUrl}task-sets/spawn-task-set`, payload);

  spawnEventTaskSet = (payload: SpawnTaskSetRequest) =>
    this.http.post<unknown>(`${this.apiUrl}task-sets/spawn-event-task-set`, payload);

  copyTaskSet = (payload: CopyTaskSetRequest) =>
    this.http.post<unknown>(`${this.apiUrl}task-sets/copy`, payload);

  deleteTaskSet = (id: number, reason: string) =>
    this.http.delete<unknown>(`${this.apiUrl}task-sets/${id}`, {
      body: { reason },
    });

  recallTaskSet = (payload: TaskSetRecallRequest) =>
    this.http.post<unknown>(`${this.apiUrl}recall-deployed-task-set`, payload);

  eventTaskInfo = (params: EventTaskSpawnRequest) =>
    this.http.post<EventTaskInfoResponse>(
      `${this.apiUrl}task-sets/event-task-set-spawn-info`,
      params,
    );

  getClosedTaskSets = (req: ClosedTaskRequest) => {
    let url = `${this.apiUrl}closed-task-sets?locationID=${req.locationID}&completedStartDate=${req.completedStartDate}&completedEndDate=${req.completedEndDate}`;

    if (req.completedStartDate) {
      url += `&completedStartDate=${req.completedStartDate}`;
    }

    if (req.completedEndDate) {
      url += `&completedEndDate=${req.completedEndDate}`;
    }

    if (req.dueStartDate) {
      url += `&dueStartDate=${req.dueStartDate}`;
    }

    if (req.dueEndDate) {
      url += `&dueEndDate=${req.dueEndDate}`;
    }

    if (req.taskType) {
      url += `&taskType=${req.taskType}`;
    }
    return this.http.get<ClosedTaskSetResponse>(url).pipe(map(res => res.closedTaskSets));
  };

  reassignTaskSet = (payload: ReassignTaskSetRequest) =>
    this.http.post<unknown>(`${this.apiUrl}reassign-task-set`, payload);

  static getTaskSetViewFromScope(scope: TaskScope): TaskSetView {
    switch (scope) {
      case TaskScope.LOCAL:
        return TaskSetView.DEPLOYED;
      case TaskScope.GLOBAL:
        return TaskSetView.GLOBAL;
      case TaskScope.DEPLOYED:
        return TaskSetView.DEPLOYED;
      case TaskScope.EVENT:
        return TaskSetView.EVENT;
      case TaskScope.COMPLETED:
        return TaskSetView.COMPLETED;
      case TaskScope.FAILED:
        return TaskSetView.FAILED;
      case TaskScope.SPAWNED:
        return TaskSetView.SPAWNED;
      default:
        return TaskSetView.DEPLOYED;
    }
  }
}
